import type { FC } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { Badge } from '@interstate/components/Badge';
import { CheckBox } from '@interstate/components/CheckBox';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { ThemeProvider } from 'styled-components';
import * as dealTemplateSelectors from '@makemydeal/dr-dash-store/selectors/dealTemplateSelectors';
import { TEST_IDS } from '../../constants';
import { formatVehicleYMMT, formatPaymentDetails } from '../../../scenarioFormatUtils';
import {
    SelectorContainer,
    ScenarioList,
    ScenarioItem,
    ScenarioLeft,
    VehicleLabel,
    PaymentDetails
} from './DealScenarioSelector.style';

export type DealScenarioSelectorProps = {
    selectedScopeIds: string[];
    onSelectedScopeIdsChange: (scopeIds: string[]) => void;
};

export const DealScenarioSelector: FC<DealScenarioSelectorProps> = ({ selectedScopeIds, onSelectedScopeIdsChange }) => {
    const theme = useInterstateTheme();
    const scenarioVehicles = useSelector(dealTemplateSelectors.getScenarioVehicleDetails);

    if (scenarioVehicles.length <= 1) {
        return null;
    }

    const handleToggleScenario = (scopeId: string) => {
        if (selectedScopeIds.includes(scopeId)) {
            onSelectedScopeIdsChange(selectedScopeIds.filter((id) => id !== scopeId));
        } else {
            onSelectedScopeIdsChange([...selectedScopeIds, scopeId]);
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <SelectorContainer data-testid={TEST_IDS.DEAL_SCENARIO_SELECTOR}>
                <Typography variant="h5" sx={{ mb: 1 }}>
                    Deal Scenario(s)
                </Typography>
                <ScenarioList data-testid={TEST_IDS.DEAL_SCENARIO_LIST}>
                    {scenarioVehicles.map((detail, index) => {
                        const isChecked = detail.isPrimary || selectedScopeIds.includes(detail.scopeId);
                        return (
                            <ScenarioItem
                                key={detail.scopeId}
                                data-testid={`${TEST_IDS.DEAL_SCENARIO_ITEM}-${index}`}
                                $isLast={index === scenarioVehicles.length - 1}
                            >
                                <ScenarioLeft>
                                    <CheckBox
                                        checked={isChecked}
                                        value={detail.scopeId}
                                        data-testid={`${TEST_IDS.DEAL_SCENARIO_CHECKBOX}-${index}`}
                                        disabled={detail.isPrimary}
                                        onChange={() => handleToggleScenario(detail.scopeId)}
                                    />
                                    <VehicleLabel>
                                        <Typography variant="strong-sm" tag="span">
                                            {formatVehicleYMMT(detail)}
                                        </Typography>
                                        {detail.isPrimary && (
                                            <Badge variant="info" data-testid={`${TEST_IDS.DEAL_SCENARIO_PRIMARY_BADGE}-${index}`}>
                                                Primary
                                            </Badge>
                                        )}
                                    </VehicleLabel>
                                </ScenarioLeft>
                                {detail.offerType && (
                                    <PaymentDetails>
                                        <Typography variant="body-sm" tag="span" color="sem.color.on-surface.muted">
                                            {formatPaymentDetails(detail)}
                                        </Typography>
                                    </PaymentDetails>
                                )}
                            </ScenarioItem>
                        );
                    })}
                </ScenarioList>
            </SelectorContainer>
        </ThemeProvider>
    );
};
